'use client';
import Link from 'next/link';
import { getDisplayName } from '@/lib/displayName';

interface Props { specialist: any; }

const TIER_COLOR: Record<string, string> = {
  'Старт': '#62c887',
  'Про': '#5ecdd0',
  'Бизнес': '#edb84a',
};

const getAvatarColor = (name: string) => {
  const colors = ['#307479', '#7c3aed', '#2563eb', '#e11d48', '#d97706', '#059669', '#4f46e5', '#0891b2'];
  return colors[name.charCodeAt(0) % colors.length];
};

const getMainCategory = (profileCategory: any, services: any[]): string | null => {
  if (profileCategory?.name) return profileCategory.name;
  if (!services?.length) return null;
  const freq: Record<string, { name: string; count: number }> = {};
  for (const s of services) {
    const catId = s.category?.id ?? s.categoryId;
    const catName = s.category?.name;
    if (catId && catName) {
      if (!freq[catId]) freq[catId] = { name: catName, count: 0 };
      freq[catId].count++;
    }
  }
  const entries = Object.values(freq);
  if (!entries.length) return null;
  return entries.sort((a, b) => b.count - a.count)[0].name;
};

const formatNextSlot = (nextSlotAt: string | null, timezone?: string) => {
  if (!nextSlotAt) return null;
  const d = new Date(nextSlotAt);
  if (d < new Date()) return null;
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const todayStr = new Date().toLocaleDateString('sv-SE', { timeZone: tz });
  const tomorrowDate = new Date(); tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  const tomorrowStr = tomorrowDate.toLocaleDateString('sv-SE', { timeZone: tz });
  const slotStr = d.toLocaleDateString('sv-SE', { timeZone: tz });
  if (slotStr === todayStr) return 'Сегодня, ' + d.toLocaleTimeString('ru-RU', { timeZone: tz, hour: '2-digit', minute: '2-digit' });
  if (slotStr === tomorrowStr) return 'Завтра, ' + d.toLocaleTimeString('ru-RU', { timeZone: tz, hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('ru-RU', { timeZone: tz, day: 'numeric', month: 'short' }) + ', ' + d.toLocaleTimeString('ru-RU', { timeZone: tz, hour: '2-digit', minute: '2-digit' });
};

/**
 * Адаптивная строка каталога. Высота стабильна (~72px) на всех ширинах,
 * состав информации растёт с шириной контейнера:
 *  - base (<640):  аватар · имя✓ · ★рейтинг · город · «от N ₽» · шеврон
 *  - sm  (≥640):  + ближайший слот, кнопка «Записаться» вместо шеврона
 *  - md  (≥768):  + категория, число отзывов
 *  - lg  (≥1024): + опыт, название самой доступной услуги
 */
export default function SpecialistRow({ specialist }: Props) {
  const { user, city, rating, reviewsCount, services, nextSlotAt, planBadge, username, timezone, category, experienceYears } = specialist;
  const displayName = getDisplayName(user);
  const profileHref = username ? '/u/' + username : '/specialists/' + specialist.id;
  const tierColor = TIER_COLOR[planBadge ?? ''] ?? TIER_COLOR['Старт'];
  const nextSlot = formatNextSlot(nextSlotAt ?? null, timezone);
  const mainCategory = getMainCategory(category, services);

  const cheapest = (services ?? []).reduce((best: any, s: any) => {
    const p = Number(s.price);
    if (isNaN(p)) return best;
    return best === null || p < Number(best.price) ? s : best;
  }, null as any);
  const minPrice = cheapest ? Number(cheapest.price) : null;

  const expStr = experienceYears > 0
    ? experienceYears + ' ' + (experienceYears === 1 ? 'год' : experienceYears < 5 ? 'года' : 'лет')
    : null;

  const apiBase = process.env.NEXT_PUBLIC_API_URL?.replace('/api/v1', '') ?? '';
  const resolveUrl = (url: string) => (url?.startsWith('http') ? url : apiBase + url);
  const avatarUrl = user?.avatarUrl ? resolveUrl(user.avatarUrl) : null;

  return (
    <Link
      href={profileHref}
      className="flex w-full items-center gap-3 px-3 py-2.5 bg-bg-surface border border-border rounded-xl hover:border-brand/50 transition-colors min-w-0"
      style={{ minHeight: 72 }}
    >
      <div className="relative flex-shrink-0" style={{ width: 48, height: 48 }}>
        {avatarUrl ? (
          <img src={avatarUrl} alt={displayName} className="w-12 h-12 rounded-full object-cover block" />
        ) : (
          <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-base"
            style={{ background: getAvatarColor(displayName) }}>
            {displayName.charAt(0).toUpperCase()}
          </div>
        )}
        <span
          title={planBadge || 'Старт'}
          className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-bg-surface"
          style={{ background: tierColor }}
        />
      </div>

      {/* Имя + вторичная строка */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-sm font-semibold text-text-primary truncate">{displayName}</span>
          {user?.isVerified && (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" className="flex-shrink-0">
              <circle cx="12" cy="12" r="11" fill="#22c55e" />
              <polyline points="7,12 10.5,15.5 17,9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
          {mainCategory && <span className="hidden md:inline text-xs text-brand font-medium truncate">· {mainCategory}</span>}
        </div>
        <div className="flex items-center gap-2 text-xs text-text-muted mt-0.5 min-w-0 overflow-hidden whitespace-nowrap">
          {rating > 0 && (
            <span className="text-amber-500 font-medium flex-shrink-0">
              ★ {Number(rating).toFixed(1)}
              {reviewsCount > 0 && <span className="hidden md:inline text-text-muted font-normal"> ({reviewsCount})</span>}
            </span>
          )}
          {city && <span className="truncate">{city}</span>}
          {expStr && <span className="hidden lg:inline flex-shrink-0">· опыт {expStr}</span>}
          {nextSlot && <span className="hidden sm:inline flex-shrink-0 text-brand">· {nextSlot}</span>}
        </div>
      </div>

      {/* Самая доступная услуга — только на широких экранах */}
      {cheapest?.title && (
        <div className="hidden lg:block flex-shrink-0 max-w-[220px] text-right">
          <div className="text-xs text-text-muted truncate">{cheapest.title}</div>
        </div>
      )}

      {/* Цена + действие */}
      <div className="flex items-center gap-3 flex-shrink-0">
        {minPrice !== null && (
          <span className="text-sm font-semibold text-text-primary whitespace-nowrap tabular-nums">
            от {minPrice.toLocaleString('ru-RU')} ₽
          </span>
        )}
        <span className="hidden sm:inline px-3 py-1.5 rounded-lg bg-brand text-white text-xs font-semibold whitespace-nowrap">
          Записаться
        </span>
        <svg className="sm:hidden w-4 h-4 text-text-muted flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
        </svg>
      </div>
    </Link>
  );
}
